import { UsageService } from '../../../../core/usage/usage.service';
import { carriedProvider } from 'src/app/core/utils/billing-line-provider';
import { Component, EventEmitter, Input, Output, QueryList, ViewChildren, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash';
import { EMPTY, of, switchMap, takeUntil, tap } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { MedpraxService } from 'src/app/core/invoicing/medprax.invoicing.service';
import { FeedbackService } from 'src/app/core/services/feedback.service';
import { PatientFinancalBillingService } from 'src/app/core/services/patient-financal-billing.service';
import { PatientFinancialService } from 'src/app/core/services/patient-financial.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { ChangeDetectorRef } from '@angular/core';
import { UserService } from 'src/app/core/services/user.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { GroupService } from 'src/app/core/services/group.service';
import { FileObject } from 'src/app/core/interfaces';
import { FileDownloadService } from 'src/app/core/services/file-download.service';
import { MedicalStatementDialogComponent } from '../medical-statement-dialog/medical-statement-dialog.component';
import { CapturePaymentComponent } from '../dispute-form/capture-payment/capture-payment.component';
import { RemoveInvoiceDialogComponent, RemoveInvoiceDialogData, RemoveInvoiceDialogResult } from './remove-invoice-dialog/remove-invoice-dialog.component';
import { DisputeFormComponent } from '../dispute-form/dispute-form.component';
import { resolveVatRate, vatRateForGroup } from 'src/app/core/utils/vat.util';
import { MONEY_PAYMENT_METHODS, canChangePaymentMethod } from 'src/app/core/utils/receipt-payment-method';
import { ReceiptPaymentMethodService } from 'src/app/core/services/receipt-payment-method.service';
import { PatientCreditService } from 'src/app/core/services/patient-credit.service';
import { ConfirmationDialogComponent } from 'src/app/core/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-patient-invoicing-list',
  templateUrl: './patient-invoicing-list.component.html',
  styleUrls: ['./patient-invoicing-list.component.scss']
})
export class InvoiceComponent extends BaseComponent {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);
  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;

  @Input() initialExpanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();
  expanded?: boolean;
  tariffCode = "1210";
  planOptionCode = "90H";
  disciplineCode = "024"
  quoteResult: any;
  consentData!: any[] | null;
  user: any;

  billingData!: any[] | null;
  patient: any;
  patientBillingData!: any[] | null;
  patientBillingMedia: any[] = [];

  fileObjects: { name: string; url: string; type: string; uploaded: boolean }[] = [];
  public expandedHeader: string = 'invoices';

  userProfile: any;
  group: any;
  selectedDoctor: any = {};
  groupUsers: any[] = [];
  unsettledAmount: number = 1000;
  isSettled: boolean = true;

  sortedBillingAid: any = {
    quotations: [],
    invoices: [],
    statements: [],
    receipts: []
  };

  totalOutstanding: number = 0;
  availableCredit: number = 0;

  // New properties for the new billing system
  newBillingDocuments: any[] = [];
  newPayments: any[] = [];
  newPatientBalance: any = null;
  isLoadingNewBilling: boolean = false;

  sendingDetails = { name: '', email: '' };


  constructor(
    private cdr: ChangeDetectorRef,
    private medpraxService: MedpraxService,
    private patientService: PatientService,
    private userService: UserService,
    private groupService: GroupService,
    private patientFinancalBillingService: PatientFinancalBillingService,
    private patientFinancialService: PatientFinancialService,
    private fileDownloadService: FileDownloadService,
    private receiptPaymentMethodService: ReceiptPaymentMethodService,
    private patientCreditService: PatientCreditService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedPatient => {
        // Clear billing data immediately when patient changes
        if (selectedPatient) {
          const previousPatientId = this.patient?.id;
          this.patient = selectedPatient;
          this.availableCredit = this.patient.availableCredit;

          // Prefill email form with patient details
          this.sendingDetails.name = `${this.patient.name} ${this.patient.surname}`;
          this.sendingDetails.email = this.patient.email || '';

          // If patient ID changed, clear billing data
          if (previousPatientId !== selectedPatient.id) {
            this.newBillingDocuments = [];
            this.newPayments = [];
            this.newPatientBalance = null;
            this.sortedBillingAid = {
              quotations: [],
              invoices: [],
              statements: [],
              receipts: []
            };
            this.totalOutstanding = 0;
            this.availableCredit = 0;
          }
        } else {
          // No patient selected, clear all data
          this.patient = null;
          this.sendingDetails = { name: '', email: '' };
          this.newBillingDocuments = [];
          this.newPayments = [];
          this.newPatientBalance = null;
          this.sortedBillingAid = {
            quotations: [],
            invoices: [],
            statements: [],
            receipts: []
          };
          this.totalOutstanding = 0;
          this.availableCredit = 0;
        }
      });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          this.user = user;
        }else{
          this.user = null;
        }
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        if (group) {
          this.group = group;
        }
      });

  }

  ngOnInit() {
    // Subscribe to real-time updates from the new service
    this.patientFinancialService.billingDocuments$.subscribe({
      next: (documents) => {
        // Add processing status flag to each document
        this.newBillingDocuments = documents.map((doc: any) => ({
          ...doc,
          hasProcessingPdf: this.checkHasProcessingPdf(doc.media || [])
        }));
        this.processNewBillingDocuments();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error receiving billing document updates:', error);
      }
    });

    this.patientFinancialService.payments$.subscribe({
      next: (payments) => {
        this.newPayments = payments;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error receiving payment updates:', error);
      }
    });

    this.patientFinancialService.patientBalance$.subscribe({
      next: (balance) => {
        this.newPatientBalance = balance;
        this.calculateNewFinancialSummary();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error receiving balance updates:', error);
      }
    });

    this.cdr.detectChanges(); // Force update UI
  }

  onRecipientSelected(recipient: { name: string, email: string }) {
    this.sendingDetails.name = recipient.name;
    this.sendingDetails.email = recipient.email;
  }


  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  toggleHeader(item: string) {
    this.expandedHeader = this.expandedHeader === item ? '' : item;
  }

  parseFloat(value: any): number {
    return parseFloat(value) || 0;
  }



  // Process new billing documents and organize them under correct headings
  processNewBillingDocuments(): void {
    if (!this.newBillingDocuments || this.newBillingDocuments.length === 0) {
      this.sortedBillingAid = {
        quotations: [],
        invoices: [],
        statements: [],
        receipts: []
      };
      // Calculate financial summary even when no documents
      this.calculateNewFinancialSummary();
      return;
    }

    // Group documents by type and date
    const groupedByType: any = {
      quotations: [],
      invoices: [],
      statements: [],
      receipts: []
    };

    this.newBillingDocuments.forEach(document => {
      const documentType = document.documentType;
      let targetArray: string;

      switch (documentType) {
        case 'estimate':
          targetArray = 'quotations';
          break;
        case 'invoice':
          targetArray = 'invoices';
          break;
        case 'receipt':
          targetArray = 'receipts';
          break;
        default:
          targetArray = 'statements';
          break;
      }

      // Create a record compatible with the existing template structure
      // hasProcessingPdf is already computed in ngOnInit subscription
      const record = {
        ...document,
        lineItems: document.lineItems || [],
        paymentAllocations: document.paymentAllocations || [],
        createdBy: document.User ? `${document.User.title} ${document.User.name} ${document.User.surname}` : 'Unknown'
      };

      groupedByType[targetArray].push(record);
    });

    // Group by date for each type
    this.sortedBillingAid = {
      quotations: this.groupDocumentsByDate(groupedByType.quotations),
      invoices: this.groupDocumentsByDate(groupedByType.invoices),
      statements: this.groupDocumentsByDate(groupedByType.statements),
      receipts: this.groupDocumentsByDate(groupedByType.receipts)
    };

    // Calculate financial summary after processing documents
    this.calculateNewFinancialSummary();
  }

  // Group documents by date
  groupDocumentsByDate(documents: any[]): any[] {
    if (!documents || documents.length === 0) return [];

    const groupedByDate: { [key: string]: any[] } = {};

    documents.forEach(document => {
      const dateKey = this.formatDate(document.issueDate);
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }
      groupedByDate[dateKey].push(document);
    });

    // Convert to sorted array
    return Object.keys(groupedByDate)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map(date => ({
        date: new Date(date),
        records: groupedByDate[date]
      }));
  }

  // Calculate financial summary using new data
  calculateNewFinancialSummary(): void {
    // Calculate total outstanding from invoices in the component
    this.totalOutstanding = this.calculateTotalOutstandingFromInvoices();

    // Use available credit from patient balance if available, otherwise from patient data
    if (this.newPatientBalance) {
      this.availableCredit = this.newPatientBalance.totalCredits || 0;
    } else if (this.patient) {
      this.availableCredit = this.patient.availableCredit || 0;
    } else {
      this.availableCredit = 0;
    }

  }

  // Getter for net balance (total outstanding minus available credit)
  get netBalance(): number {
    return this.totalOutstanding - this.availableCredit;
  }

  // Calculate total outstanding from invoices in the component
  private calculateTotalOutstandingFromInvoices(): number {
    if (!this.newBillingDocuments || this.newBillingDocuments.length === 0) {
      return 0;
    }

    // Filter for invoices and estimates that have outstanding balances (exclude removed documents)
    const documentsWithOutstanding = this.newBillingDocuments.filter(document =>
      (document.documentType === 'invoice') &&
      document.active === true &&
      !document.removedAt && // Exclude removed documents
      (parseFloat(document.outstandingBalance) || 0) > 0
    );

    const totalOutstanding = documentsWithOutstanding.reduce((total, document) => {
      const outstandingBalance = parseFloat(document.outstandingBalance) || 0;
      return total + outstandingBalance;
    }, 0);

    return totalOutstanding;
  }

  // Utility to format date into 'YYYY-MM-DD'
  formatDate(dateStr: string): string {
    return new Date(dateStr).toISOString().split('T')[0];
  }

  sendingBillingRecord(record: any): void {
    if (!this.sendingDetails.email) {
      this.showToastMessage('An email address was not provided. Please check the relevant email field and try again.');
      return;
    }

    if (!this.sendingDetails.name) {
      this.showToastMessage('A recipient name was not provided. Please check the relevant name field and try again.');
      return;
    }

    // Get the first PDF media from the record
    const pdfMedia = this.getFirstPdfMedia(record.media || []);

    if (!pdfMedia) {
      this.showToastMessage('No PDF document found for this billing record.');
      return;
    }

    this.patientFinancialService.sendBillingDocumentEmail(
      record.id,
      this.sendingDetails.name,
      this.sendingDetails.email
    )
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          this.showToastMessage('Email sent successfully');
          this.closeAllMenus(this.menuTriggers);
        },
        error: (error) => {
          console.error('Error sending billing document email:', error);
          this.showToastMessage('There was an error sending the email. Please try again.');
        }
      });
  }

  downloadPdf(mediaItem: any) {
    if (!mediaItem) {
      console.warn("No PDF available for download.");
      return;
    }

    // Handle different media structures
    let pdfUrl = '';

    if (mediaItem.originalPdfUrl) {
      // If it's an image with a PDF original, use the original PDF
      pdfUrl = mediaItem.originalPdfUrl;
    } else if (mediaItem.mediaUrl && mediaItem.mediaType === 'application/pdf') {
      // If it's a PDF, use the media URL
      pdfUrl = mediaItem.mediaUrl;
    } else {
      console.warn("No valid PDF file found.");
      return;
    }

    // Open in a new tab
    window.open(pdfUrl, "_blank");
  }

  // Helper method to find the first PDF media item from a record's media array
  getFirstPdfMedia(mediaArray: any[]): any {
    if (!mediaArray || mediaArray.length === 0) {
      return null;
    }

    // First try to find a PDF
    const pdfMedia = mediaArray.find(media => media.mediaType === 'application/pdf');
    if (pdfMedia) {
      return pdfMedia;
    }

    // If no PDF found, look for an image with originalPdfUrl
    const imageWithPdf = mediaArray.find(media =>
      media.mediaType.startsWith('image/') && media.originalPdfUrl
    );
    if (imageWithPdf) {
      return imageWithPdf;
    }

    // Fallback to first media item
    return mediaArray[0];
  }

  public openCapturePayment(record: any = {}): void {
    // Get the group ID for the payment
    if (!this.group?.id) {
      this.showToastMessage('Group information not available');
      return;
    }

    this.dialog.open(CapturePaymentComponent, {
      width: '600px',
      //height: '80vh',
      data: {
        patientId: this.patient.id,
        groupId: this.group.id,
        patient: this.patient,
        group: this.group,
        patientName: `${this.patient.name} ${this.patient.surname}`,
        specificInvoice: record,
        //isActive: false
      },
      //panelClass: 'payment-dialog-container'
    })
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (!result) {
            this.showToastMessage('Payment Cancelled');
            return;
          }

          if (result.success) {
            // Payment was captured using the new service
            this.showToastMessage('Payment captured successfully');
            this.calculateNewFinancialSummary();
          } else {
            // Payment capture failed
            console.error('Payment capture failed:', result.error);
            this.showToastMessage(result.error || 'Payment capture failed');
          }
        },
        error: (error) => {
          this.showToastMessage('There was an error capturing payment');
          console.error('Payment capture error:', error);
        }
      });
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  override closeAllMenus(menuTriggers: QueryList<MatMenuTrigger>): void {
    menuTriggers.forEach(trigger => trigger.closeMenu());
  }

  openStatementDialog() {
    this.usage.flow('statement', 'start');
    setTimeout(() => {
      const dialogRef = this.dialog.open(MedicalStatementDialogComponent, {
        width: '35vw',
        maxHeight: '65vh',
        data: {
          patient: this.patient,
          isActive: false,
          patientName: `${this.patient.name} ${this.patient.surname}`,
          patientId: this.patient.id,
          totalOutstanding: this.totalOutstanding
        }
      })
        .afterClosed()
        .subscribe(res => {
          //Prevent errors if dialog is closed without selection
          if (res) {
            this.printStatement(res);
            this.calculateNewFinancialSummary();

          }
        })
    })
  }

  printStatement(date: any) {
    if (!this.patient || !date.startDate || !date.endDate) {
      this.showToastMessage('Invalid date range or patient data!');
      return;
    }

    // Refresh billing documents to ensure we have the latest data with all associations
    // This ensures receipts have their PatientPayment associations loaded
    this.patientFinancialService.loadPatientBillingDocuments(this.patient.id).subscribe({
      next: (response) => {
        // Update local array with refreshed data
        if (response.data) {
          this.newBillingDocuments = response.data.map((doc: any) => ({
            ...doc,
            hasProcessingPdf: this.checkHasProcessingPdf(doc.media || [])
          }));
          this.processNewBillingDocuments();
          this.cdr.detectChanges();
        }
        // Proceed with statement generation after refresh
        this.generateStatementAfterRefresh(date);
      },
      error: (error) => {
        console.error('Error refreshing billing documents:', error);
        // Proceed anyway with current data
        this.generateStatementAfterRefresh(date);
      }
    });
  }

  private generateStatementAfterRefresh(date: any) {
    if (!this.patient || !date.startDate || !date.endDate) {
      return;
    }

    // Filter billing documents within the date range
    const startDate = new Date(date.startDate);
    const endDate = new Date(date.endDate);
    const vatExclusive = date.vatExclusive === true;

    // Get invoices and receipts within the date range (exclude removed documents)
    const filteredDocuments = this.newBillingDocuments.filter(document => {
      const documentDate = new Date(document.issueDate);
      return documentDate >= startDate && documentDate <= endDate &&
        (document.documentType === 'invoice' || document.documentType === 'receipt') &&
        !document.removedAt; // Exclude removed documents
    });

    if (filteredDocuments.length === 0) {
      this.showToastMessage('No billing documents found for the selected date range');
      return;
    }

    // Calculate aging summary
    const agingSummary = this.calculateAgingSummary(filteredDocuments);

    // Prepare line items from all documents
    const allLineItems: any[] = [];
    filteredDocuments.forEach(document => {
      if (document.PatientBillingLineItems && document.PatientBillingLineItems.length > 0) {
        document.PatientBillingLineItems.forEach((lineItem: any) => {
          allLineItems.push({
            ...lineItem,
            documentType: document.documentType,
            billingNumber: document.billingNumber,
            issueDate: document.issueDate,
            createdAt: document.createdAt || document.issueDate, // Use createdAt for chronological sorting
            totalDue: document.totalDue,
            amountPaid: document.amountPaid,
            outstandingBalance: document.outstandingBalance,
            // Include payment association for receipts (needed for statement display)
            payment: document.PatientPayment || (document as any).payment
          });
        });
      }
    });

    // Generate unique billing number for statement
    const uniqueCode = this.patientFinancalBillingService.createUniqueCode();
    const statementBillingNumber = `STMT-${uniqueCode}`;

    // Prepare data for new billing document (statement)
    const statementBillingData = {
      patientId: this.patient.id,
      groupId: this.group.id,
      providerId: this.user.id,
      documentType: 'statement' as 'estimate' | 'invoice' | 'receipt' | 'statement',
      billingDate: new Date(),
      issueDate: new Date(),
      dueDate: new Date(),
      subtotal: 0, // Will be calculated from line items
      vatAmount: 0, // Will be calculated from line items
      totalAmount: 0, // Will be calculated from line items
      shortfallAmount: 0,
      discountType: 'percentage' as 'percentage' | 'fixed',
      discountAmount: 0,
      discountPercentage: 0,
      notes: `Statement for period ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`,
      lineItems: allLineItems,
      billingNumber: statementBillingNumber,
      vatExclusive,
      vatRate: vatRateForGroup(this.group),
    };

    // Create the statement billing document using the new service
    this.patientFinancialService.createBillingDocument(statementBillingData).subscribe({
      next: (result: any) => {
        const savedStatement = result.data;
        // Generate PDF using the new statement format
        this.generateStatementPDF(savedStatement, filteredDocuments, agingSummary, startDate, endDate, vatExclusive)
          .then((pdfBytes) => {
            // Convert PDF bytes to blob
            const pdfBlob = new Blob([pdfBytes as any], { type: 'application/pdf' });
            const fileName = `Statement-${this.patient.name}-${this.patient.surname}_${Date.now()}.pdf`;

            // Upload PDF to the new service
            this.patientFinancialService.uploadBillingDocumentPDF(savedStatement.id, this.group.id, pdfBlob, fileName)
              .subscribe({
                next: (updatedDocument) => {
                  this.showToastMessage('Statement generated successfully!');
                  this.usage.flow('statement', 'done');

                  // Trigger platform-aware download
                  void this.fileDownloadService.download(pdfBlob, fileName);
                },
                error: (uploadError) => {
                  console.error('Error uploading statement PDF:', uploadError);
                  this.showToastMessage('Statement created but error uploading PDF. Please try again.');
                }
              });
          })
          .catch((error) => {
            console.error('Error generating statement PDF:', error);
            this.showToastMessage('Statement created but error generating PDF. Please try again.');
          });
      },
      error: (error) => {
        console.error('Error creating statement:', error);
        this.showToastMessage('Failed to create statement: ' + error.message);
      }
    });
  }

  // Calculate aging summary for outstanding invoices
  private calculateAgingSummary(documents: any[]): any {
    const now = new Date();
    const aging = {
      current: 0,
      days30: 0,
      days60: 0,
      days90: 0,
      days120Plus: 0
    };

    documents.forEach(document => {
      if (document.documentType === 'invoice' && document.outstandingBalance > 0) {
        const documentDate = new Date(document.issueDate);
        const daysDiff = Math.floor((now.getTime() - documentDate.getTime()) / (1000 * 60 * 60 * 24));
        const outstandingAmount = parseFloat(document.outstandingBalance) || 0;

        if (daysDiff <= 30) {
          aging.current += outstandingAmount;
        } else if (daysDiff <= 60) {
          aging.days30 += outstandingAmount;
        } else if (daysDiff <= 90) {
          aging.days60 += outstandingAmount;
        } else if (daysDiff <= 120) {
          aging.days90 += outstandingAmount;
        } else {
          aging.days120Plus += outstandingAmount;
        }
      }
    });

    return aging;
  }

  // Generate statement PDF with new format
  private async generateStatementPDF(statementDocument: any, documents: any[], agingSummary: any, startDate: Date, endDate: Date, vatExclusive: boolean = false): Promise<Uint8Array> {
    // Calculate totals from documents
    const subTotal = documents.reduce((sum: number, doc: any) => sum + (parseFloat(doc.subtotal) || 0), 0);
    const vat = documents.reduce((sum: number, doc: any) => sum + (parseFloat(doc.vat) || 0), 0);
    const totalDue = documents.reduce((sum: number, doc: any) => sum + (parseFloat(doc.totalDue) || 0), 0);
    // Use the existing PDF generation service but with statement-specific data
    const statementData = {
      patientId: this.patient.id,
      startDate: startDate,
      endDate: endDate,
      billingNumber: statementDocument.billingNumber,
      patient: this.patient,
      practiceInfo: {
        practiceName: this.group.companyName || this.group.name,
        practiceNumber: this.group.practiceNumber,
        email: this.group.practiceEmail,
        tel: this.group.practicePhone,
      },
      transactions: documents, // Pass the filtered documents
      agingSummary: agingSummary,
      subTotal: subTotal,
      vat: vat,
      totalDue: totalDue,
      createdBy: `${this.user.title} ${this.user.name} ${this.user.surname}`,
      user: this.user,
      group: this.group,
      vatExclusive,
      vatRate: resolveVatRate(statementDocument?.vatRate, this.group),
    };

    // Use the existing statement PDF generation but with new format
    return this.patientFinancalBillingService.generateStatementPDF(statementData);
  }

  convertToInvoice(record: any): void {
    // Validate that this is an estimate that can be converted
    if (record.documentType !== 'estimate') {
      this.showToastMessage('Only estimates can be converted to invoices.');
      return;
    }

    // Get the group for the due date
    if (!this.group?.id) {
      this.showToastMessage('Group information not available.');
      return;
    }

    // Calculate due date (7 days from now by default)
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 7);

    // Extract line items from PatientBillingLineItems (the actual line items from the estimate)
    const estimateLineItems = record.PatientBillingLineItems || [];

    // Prepare line items from the estimate - preserve original values exactly as they were
    const lineItems = estimateLineItems.map((item: any) => ({
      description: item.description,
      quantity: item.quantity || 1,
      quantityUnit: item.quantityUnit || null,
      unitPrice: item.unitPrice || 0,
      totalAmount: item.totalAmount,
      balance: item.totalAmount, // For compatibility with PDF generation
      code: item.code,
      codeType: item.codeType || 'procedure',
      tariffCode: item.code,
      tariffDescription: item.description,
      nappiCode: item.nappiCode,
      nappiDescription: item.nappiDescription,
      icdCodes: item.icdCodes || [],
      ...carriedProvider(item),
      appointmentId: item.appointmentId,
      hospitalId: item.hospitalId,
      hospitalName: item.hospitalName,
      hospitalSetting: item.hospitalSetting,
      ruleType: item.ruleType || record.ruleType,
      ruleTypeDescription: item.ruleTypeDescription,
      modifier: item.modifier,
      modifierDescription: item.modifierDescription,
      discountType: item.discountType || record.discountType || 'percentage',
      discountAmount: item.discountAmount || 0,
      discountPercentage: item.discountPercentage || 0,
      vatAmount: item.vatAmount || 0,
      vatPercentage: item.vatPercentage ?? resolveVatRate(record.vatRate, this.group) * 100,
      totalAfterDiscount: item.totalAfterDiscount || item.totalAmount,
      totalAfterVat: item.totalAfterVat || item.totalAmount,
      date: item.date || record.issueDate
    }));

    // Calculate totals from the actual line items
    const subtotal = record.subtotal;
    const vat = record.vat;
    const totalDue = record.totalDue;
    // Inherit the estimate's rate so the converted invoice reproduces the figures the
    // patient was quoted, rather than re-deriving from the group's current status.
    const vatRate = resolveVatRate(record.vatRate, this.group);

    // Prepare data for new service (same as dispute form)
    const billingData = {
      billingNumber: record.billingNumber,
      patientId: record.patientId,
      groupId: record.groupId,
      providerId: record.providerId || record.userId, // Use providerId if available, otherwise fallback to userId
      priceGroupId: record.priceGroupId,
      documentType: 'invoice' as 'estimate' | 'invoice' | 'receipt' | 'statement',
      billingDate: new Date(), // Use current date
      issueDate: new Date(), // Use current date
      dueDate: dueDate, // Use calculated due date
      subtotal: subtotal,
      vatAmount: vat,
      totalAmount: totalDue,
      shortfallAmount: 0,
      discountType: record.discountType || 'percentage',
      discountAmount: record.discount || 0,
      discountPercentage: record.discount || 0,
      notes: record.notes,
      lineItems: lineItems,
      appointmentId: record.appointmentId,
      vatExclusive: record.vatExclusive === true,
      vatRate: vatRate,
    };

    // First, save the billing document using the new service (same as dispute form)
    this.patientFinancialService.createBillingDocument(billingData).subscribe({
      next: (result: any) => {
        const savedDocument = result.data;
        // Now generate PDF using the old service for compatibility (same as dispute form)
        const currentGroup = this.group;
        const plainUser = {
          title: this.user?.title,
          name: this.user?.name,
          surname: this.user?.surname,
        };

        const pdfBillingData = {
          patientId: this.patient.id,
          type: 'Invoice',
          appointment: record.appointmentId,
          status: 'Pending',
          issueDate: record.issueDate,
          dueDate: dueDate,
          priceGroup: record.priceGroupId,
          discount: record.discount || 0,
          discountType: record.discountType || 'percentage',
          notes: record.notes,
          lineItems: lineItems,
          ruleType: record.ruleType || 'OH',
          totalDue: totalDue.toFixed(2) || 0,
          patient: this.patient, // Use the Patient object from the record
          group: currentGroup,
          user: plainUser,
          billingNumber: savedDocument.billingNumber,
          provider: savedDocument.Provider,
          hospital: record.hospitalId,
          vatExclusive: record.vatExclusive === true,
          vatRate: savedDocument.vatRate ?? billingData.vatRate,
          practiceInfo: {
            practiceName: this.group.companyName || this.group.name,
            practiceNumber: this.group.practiceNumber,
            email: this.group.practiceEmail,
            tel: this.group.practicePhone,
          }
        };

        // Generate and download PDF (same as dispute form)
        this.patientFinancalBillingService.generateBillingPDF(savedDocument.id, pdfBillingData)
          .then((pdfBytes) => {
            // Convert PDF bytes to blob
            const pdfBlob = new Blob([pdfBytes as any], { type: 'application/pdf' });
            const fileName = `Invoice-${record.Patient?.name}-${record.Patient?.surname}_${Date.now()}.pdf`;

            // Upload PDF to the new service (same as dispute form)
            this.patientFinancialService.uploadBillingDocumentPDF(savedDocument.id, record.groupId, pdfBlob, fileName)
              .subscribe({
                next: (updatedDocument) => {
                  // Trigger platform-aware download
                  void this.fileDownloadService.download(pdfBlob, fileName);
                  this.showToastMessage('Invoice created successfully and PDF downloaded!');
                  void this.patientCreditService.offer(savedDocument, this.group);
                },
                error: (uploadError) => {
                  console.error('Error uploading PDF:', uploadError);
                  this.showToastMessage('Invoice created but error uploading PDF. Please try again.');
                }
              });
          })
          .catch((error) => {
            console.error('Error generating PDF:', error);
            this.showToastMessage('Invoice created but error generating PDF. Please try again.');
          });
      },
      error: (error) => {
        console.error('Error creating invoice:', error);
        this.showToastMessage('Failed to create invoice: ' + error.message);
      }
    });
  }

  openRemoveInvoiceDialog(record: any): void {
    // Check if invoice has payments
    const hasPayments = this.checkInvoiceHasPayments(record);
    const paymentDetails = hasPayments ? this.getPaymentDetails(record) : [];

    const dialogData: RemoveInvoiceDialogData = {
      invoice: record,
      patient: this.patient,
      group: this.group,
      user: this.user,
      hasPayments: hasPayments,
      paymentDetails: paymentDetails
    };

    const dialogRef = this.dialog.open(RemoveInvoiceDialogComponent, {
      width: '600px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: RemoveInvoiceDialogResult) => {
      if (result && result.action !== 'cancel') {
        this.handleInvoiceRemoval(record, result);
      }
    });
  }

  private checkInvoiceHasPayments(record: any): boolean {
    // Check if there are payment allocations
    if (record.PatientPaymentAllocations && record.PatientPaymentAllocations.length > 0) {
      return true;
    }

    // Check if amount paid is greater than 0
    if (record.amountPaid && parseFloat(record.amountPaid) > 0) {
      return true;
    }

    // Check if outstanding balance is less than total due (indicating partial payment)
    if (record.outstandingBalance && record.totalDue) {
      const outstanding = parseFloat(record.outstandingBalance);
      const total = parseFloat(record.totalDue);
      return outstanding < total;
    }

    return false;
  }

  private getPaymentDetails(record: any): any[] {
    if (record.PatientPaymentAllocations) {
      return record.PatientPaymentAllocations;
    }
    return [];
  }

  private handleInvoiceRemoval(record: any, result: RemoveInvoiceDialogResult): void {
    if (result.action === 'remove') {
      this.removeInvoice(record, result.reason);
    } else if (result.action === 'removeAndRecreate') {
      this.removeInvoice(record, result.reason, true);
    } else if (result.action === 'removeWithPayments') {
      this.removeInvoiceWithPayments(record, result.reason);
    } else if (result.action === 'removeWithPaymentsAndRecreate') {
      this.removeInvoiceWithPayments(record, result.reason, true);
    } else if (result.action === 'testPrefill') {
      this.testPrefill(record);
    }
  }

  private removeInvoice(record: any, reason: string, recreate: boolean = false): void {
    // Call backend to mark invoice as removed
    this.patientFinancialService.removeBillingDocument(record.id, reason).subscribe({
      next: (response) => {
        this.showToastMessage('Invoice removed successfully');

        // Update the local record
        record.removedAt = new Date();
        record.removedBy = `${this.user.title} ${this.user.name} ${this.user.surname}`;
        record.removalReason = reason;

        // If recreate is requested, open dispute form
        if (recreate) {
          this.recreateInvoice(record);
        }

        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error removing invoice:', error);
        this.showToastMessage('Failed to remove invoice: ' + error.message);
      }
    });
  }

  private removeInvoiceWithPayments(record: any, reason: string, recreate: boolean = false): void {
    // Show confirmation for dangerous action
    const confirmed = confirm(
      `⚠️ WARNING: This will permanently remove the invoice AND all associated payment records.\n\n` +
      `Invoice: ${record.billingNumber}\n` +
      `This action cannot be undone and may affect financial reporting.\n\n` +
      `Are you absolutely sure you want to proceed?`
    );

    if (!confirmed) {
      return;
    }

    // Call backend to remove invoice with payments
    this.patientFinancialService.removeBillingDocumentWithPayments(record.id, reason).subscribe({
      next: (response) => {
        this.showToastMessage('Invoice and payments removed successfully');

        // Update the local record
        record.removedAt = new Date();
        record.removedBy = `${this.user.title} ${this.user.name} ${this.user.surname}`;
        record.removalReason = reason;
        record.paymentsRemoved = true;

        // If recreate is requested, open dispute form
        if (recreate) {
          this.recreateInvoice(record);
        }

        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error removing invoice with payments:', error);
        this.showToastMessage('Failed to remove invoice with payments: ' + error.message);
      }
    });
  }

  private getPaymentTotal(record: any): number {
    if (record.PatientPaymentAllocations) {
      return record.PatientPaymentAllocations.reduce((sum: number, allocation: any) =>
        sum + (parseFloat(allocation.allocatedAmount) || 0), 0);
    }
    return 0;
  }

  recreateInvoice(record: any): void {
    // Open dispute form with pre-filled data
    const dialogRef = this.dialog.open(DisputeFormComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      data: {
        patient: this.patient,
        group: this.group,
        user: this.user,
        // Pre-fill with invoice data
        prefillData: {
          type: 'Invoice',
          appointment: record.appointmentId ? { id: record.appointmentId } : null,
          status: 'Pending',
          issueDate: new Date(),
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
          priceGroup: record.priceGroupId,
          discount: record.discount || 0,
          discountType: record.discountType || 'percentage',
          notes: record.notes || '',
          lineItems: record.PatientBillingLineItems || [],
          ruleType: record.ruleType || 'OH',
          hospital: record.hospitalId ? { id: record.hospitalId } : null
        }
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.showToastMessage('Invoice recreated successfully');
        // Refresh the billing documents
        this.calculateNewFinancialSummary();
      }
    });
  }

  testPrefill(record: any): void {
    // Open dispute form with pre-filled data for testing (without actually removing the invoice)
    const dialogRef = this.dialog.open(DisputeFormComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      data: {
        patient: this.patient,
        group: this.group,
        user: this.user,
        // Pre-fill with invoice data for testing
        prefillData: {
          type: 'Invoice',
          appointment: record.appointmentId ? { id: record.appointmentId } : null,
          status: 'Pending',
          issueDate: new Date(),
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
          priceGroup: record.priceGroupId,
          discount: record.discount || 0,
          discountType: record.discountType || 'percentage',
          notes: record.notes || '',
          lineItems: record.PatientBillingLineItems || [],
          ruleType: record.ruleType || 'OH',
          hospital: record.hospitalId ? { id: record.hospitalId } : null
        }
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.showToastMessage('Test prefill completed - Invoice was not actually removed');
      } else {
        this.showToastMessage('Test prefill cancelled');
      }
    });
  }

  creditAmount = 0;
  applyingCredit = false;

  // The most credit an invoice can take: what it owes, or all the credit when that is less.
  suggestedCredit(record: any): number {
    return Math.round(Math.min(this.availableCredit, parseFloat(record.outstandingBalance || 0)) * 100) / 100;
  }

  async applyCredit(record: any): Promise<void> {
    this.closeAllMenus(this.menuTriggers);
    this.applyingCredit = true;
    try {
      const { applied, failed } = await this.patientCreditService.apply(record, this.group, this.creditAmount);
      this.showToastMessage(failed
        ? `R ${applied.toFixed(2)} credit applied, but ${failed} receipt could not be filed.`
        : `R ${applied.toFixed(2)} credit applied to #${record.billingNumber}.`);
    } catch (error: any) {
      this.showToastMessage(error?.error?.message || 'The credit could not be applied.');
    } finally {
      this.applyingCredit = false;
    }
  }

  readonly moneyPaymentMethods = MONEY_PAYMENT_METHODS;
  readonly canChangePaymentMethod = canChangePaymentMethod;
  changingPaymentMethod = false;

  // Corrects a receipt captured with the wrong payment method, then redraws its PDF.
  changeReceiptPaymentMethod(record: any, method: string): void {
    const current = record.PatientPayment.paymentMethod;
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Change payment method',
        message: `Change receipt #${record.billingNumber} from ${current} to ${method}? The receipt PDF will be redrawn with the new method.`,
        confirmText: 'Change',
        cancelText: 'Cancel',
      },
    }).afterClosed().subscribe(async (confirmed) => {
      if (!confirmed) return;
      this.changingPaymentMethod = true;
      try {
        const { failed } = await this.receiptPaymentMethodService.change(record, method, this.group);
        this.showToastMessage(failed
          ? `Payment method changed to ${method}, but ${failed} receipt PDF could not be redrawn.`
          : `Payment method changed to ${method}.`);
      } catch (error: any) {
        this.showToastMessage(error?.error?.message || 'The payment method could not be changed.');
      } finally {
        this.changingPaymentMethod = false;
      }
    });
  }

  // Pro Bono detection methods
  isProBonoPayment(record: any): boolean {
    // For receipts, check if this specific receipt has Pro Bono payment method
    if (record.documentType === 'receipt' && record.PatientPayment) {
      const paymentMethod = (record.PatientPayment.paymentMethod || '').toLowerCase();
      return paymentMethod.includes('pro bono') || paymentMethod.includes('pro-bono') || paymentMethod.includes('probono');
    }

    // For invoices, check if there are any linked Pro Bono receipts with the same billing number
    if (record.documentType === 'invoice') {
      const linkedDocs = this.getLinkedDocumentsByBillingNumber(record.billingNumber);

      return linkedDocs.some(linkedDoc => {
        if (linkedDoc.documentType === 'receipt' && linkedDoc.PatientPayment) {
          const paymentMethod = (linkedDoc.PatientPayment.paymentMethod || '').toLowerCase();
          return paymentMethod.includes('pro bono') || paymentMethod.includes('pro-bono') || paymentMethod.includes('probono');
        }
        return false;
      });
    }

    return false;
  }

  getLinkedDocumentsByBillingNumber(billingNumber: string): any[] {
    if (!this.newBillingDocuments) {
      return [];
    }

    // Extract the core number from the billing number (e.g., "251007115532730" from "INV-251007115532730")
    const coreNumber = billingNumber.replace(/^(INV-|RCPT-|EST-)/, '');

    return this.newBillingDocuments.filter(doc => {
      const docCoreNumber = doc.billingNumber.replace(/^(INV-|RCPT-|EST-)/, '');
      return docCoreNumber === coreNumber;
    });
  }

  getDocumentStatusLabel(record: any): string {
    // Check for Pro Bono payments first
    const isProBono = this.isProBonoPayment(record);

    if (isProBono) {
      return 'Pro Bono';
    }

    // For receipts, show "Payment Received" instead of status
    if (record.documentType === 'receipt') {
      return 'Payment Received';
    }

    // For estimates, show "Estimate" instead of status
    if (record.documentType === 'estimate') {
      return 'Estimate';
    }

    // For invoices, use the status label
    const statusLabel = (() => {
      switch (record.status) {
        case 'paid':
          return 'Paid';
        case 'partially_paid':
          return 'Partially Paid';
        case 'pending':
          return 'Pending';
        case 'overdue':
          return 'Overdue';
        case 'draft':
          return 'Draft';
        case 'cancelled':
          return 'Cancelled';
        default:
          return 'No Invoice';
      }
    })();

    return statusLabel;
  }

  checkHasProcessingPdf(media: any[]): boolean {
    if (!media || !Array.isArray(media)) {
      return false;
    }
    return media.some((m: any) =>
      m.mediaType === 'application/pdf' && m.processingStatus === 'processing'
    );
  }

  getDocumentStatusChipClass(record: any): string {
    if (this.isProBonoPayment(record)) {
      return 'blue-chip';
    }
    if (record.documentType === 'receipt') {
      return 'green-chip';
    }
    if (record.documentType === 'estimate') {
      return 'grey-chip';
    }

    // For invoices, use status-based classes
    switch (record.status) {
      case 'paid':
        return 'green-chip';
      case 'partially_paid':
        return 'yellow-chip';
      case 'pending':
        return 'orange-chip';
      case 'overdue':
        return 'red-chip';
      case 'draft':
        return 'grey-chip';
      case 'cancelled':
        return 'red-chip';
      default:
        return 'grey-chip';
    }
  }

}
